/**
 * ResponsiveHelper — Central utility for mobile-responsive Phaser UI.
 *
 * Provides viewport-aware sizing for dialogs, fonts, and touch targets,
 * plus a reusable scrollable container with mask and touch/wheel support.
 */

import Phaser from 'phaser';

// ---------------------------------------------------------------------------
// Viewport detection
// ---------------------------------------------------------------------------

const MOBILE_BREAKPOINT = 600;

/**
 * Detect mobile/phone using four independent signals (highest-priority first):
 *   0. URL override / persisted preference (`?mobile=1`, `?desktop=1`, or
 *      localStorage['cm_platform']) — lets us test mobile on Chrome DevTools
 *      and lets the mobile-HUD scene force its own branches to activate.
 *   1. Camera width < 600 — Phaser canvas is phone-sized
 *   2. pointer: coarse — touch-primary device (no mouse)
 *   3. Physical screen narrow side < 600 — phone hardware, regardless of
 *      orientation or attached peripherals (handles Galaxy S25/DeX setups)
 */
export function isMobile(scene?: Phaser.Scene): boolean {
  // Signal 0: explicit URL / localStorage override
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      const mobileParam = params.get('mobile');
      if (mobileParam === '1' || mobileParam === 'true') return true;
      const desktopParam = params.get('desktop');
      if (desktopParam === '1' || desktopParam === 'true') return false;
      const stored = window.localStorage?.getItem('cm_platform');
      if (stored === 'mobile') return true;
      if (stored === 'desktop') return false;
    } catch { /* fall through to auto-detect */ }
  }

  // Signal 1: Phaser camera width (if scene provided)
  if (scene && scene.cameras.main.width < MOBILE_BREAKPOINT) return true;

  if (typeof window === 'undefined') return false;

  // Signal 2: touch-primary device
  if (window.matchMedia('(pointer: coarse)').matches) return true;

  // Signal 3: physical screen is phone-sized
  const narrowSide = Math.min(window.screen.width, window.screen.height);
  if (narrowSide < MOBILE_BREAKPOINT) return true;

  return false;
}

// ---------------------------------------------------------------------------
// Dialog sizing
// ---------------------------------------------------------------------------

export interface ResponsiveDims {
  width: number;
  height: number;
  x: number;
  y: number;
  isMobile: boolean;
  padding: number;
  contentWidth: number;
}

/**
 * Vertical chrome reservations on mobile so Phaser dialogs don't render
 * behind the always-on DOM overlays. These match MobileDOMOverlay's
 * top-bar height (~44px bar + ~6px iOS safe-area = ~50, plus a hair of
 * margin) and the FAB column / planet-info-bar / browser-toolbar stack at
 * the bottom (~110). When sizing/positioning a dialog, subtract these
 * from the available viewport so the dialog sits inside the visible gap.
 */
const MOBILE_TOP_RESERVED = 56;
const MOBILE_BOTTOM_RESERVED = 110;

/**
 * Compute dialog dimensions that fit the current viewport.
 *
 * - Mobile (< 600px canvas): fills the gap between the top bar and the FAB
 *   column / browser bottom toolbar (avoids the dialog title rendering
 *   behind the top bar and the close button rendering behind FABs).
 * - Desktop: uses preferred size, clamped to screen, centered.
 */
export function getResponsiveDialogSize(
  desktopWidth: number,
  desktopHeight: number,
  scene: Phaser.Scene,
): ResponsiveDims {
  const cam = scene.cameras.main;
  const vw = cam.width;
  const vh = cam.height;
  const mobile = isMobile(scene);

  const MARGIN = mobile ? 8 : 20;

  const width = mobile
    ? vw - MARGIN * 2
    : Math.min(desktopWidth, vw - MARGIN * 2);

  const topReserve = mobile ? MOBILE_TOP_RESERVED : 0;
  const bottomReserve = mobile ? MOBILE_BOTTOM_RESERVED : 0;
  // 120px floor protects landscape phones where reserves nearly equal vh.
  const availableHeight = Math.max(120, vh - topReserve - bottomReserve - MARGIN * 2);

  const height = mobile
    ? availableHeight
    : Math.min(desktopHeight, vh - MARGIN * 2);

  const x = Math.round((vw - width) / 2);
  // Anchor the dialog inside the safe gap on mobile so its header sits just
  // below the top bar; fall back to viewport-center on desktop.
  const y = mobile
    ? Math.round(topReserve + MARGIN)
    : Math.round((vh - height) / 2);

  const padding = mobile ? 10 : 15;

  return {
    width,
    height,
    x,
    y,
    isMobile: mobile,
    padding,
    contentWidth: width - padding * 2,
  };
}

// ---------------------------------------------------------------------------
// Font sizing
// ---------------------------------------------------------------------------

/**
 * Scale a font size for mobile (0.85x, min 10px).
 */
export function getResponsiveFontSize(desktopSize: number, scene: Phaser.Scene): number {
  if (!isMobile(scene)) return desktopSize;
  return Math.max(10, Math.round(desktopSize * 0.85));
}

/**
 * Compute a dialog size that fits the current viewport, returning a fit-scale
 * factor for cases where the requested size already exceeds the viewport.
 *
 * - If preferred size fits with margins → returns { width: preferredW, ..., fitScale: 1 }
 * - If preferred size overflows → returns the preferred size AND a fitScale < 1
 *   that callers apply via `container.setScale(fitScale)` so the rendered
 *   dialog physically fits on-screen without having to reflow internal layout.
 *
 * Safe-area aware (reads env(safe-area-inset-*)).
 */
export function resolveDialogSize(
  preferredW: number,
  preferredH: number,
  scene: Phaser.Scene,
  opts: { marginTop?: number; marginBottom?: number; marginSide?: number } = {},
): { width: number; height: number; fitScale: number; availW: number; availH: number } {
  const vw = scene.cameras.main.width;
  const vh = scene.cameras.main.height;
  const marginTop = opts.marginTop ?? 8;
  const marginBottom = opts.marginBottom ?? 8;
  const marginSide = opts.marginSide ?? 8;

  const availW = Math.max(100, vw - marginSide * 2);
  const availH = Math.max(100, vh - marginTop - marginBottom);

  const fitScale = Math.min(1, availW / preferredW, availH / preferredH);
  return { width: preferredW, height: preferredH, fitScale, availW, availH };
}

// ---------------------------------------------------------------------------
// Touch targets
// ---------------------------------------------------------------------------

/**
 * Ensure interactive elements meet minimum 44px touch target on mobile.
 */
export function getTouchTarget(desktopSize: number, scene: Phaser.Scene): number {
  if (!isMobile(scene)) return desktopSize;
  return Math.max(44, desktopSize);
}

// ---------------------------------------------------------------------------
// Scrollable container
// ---------------------------------------------------------------------------

export interface ScrollBounds {
  x: number;      // left edge relative to parent
  y: number;      // top edge relative to parent
  width: number;
  height: number;
}

/**
 * Create a masked, scrollable container inside a parent Container.
 *
 * Returns the inner container that you add children to. Content that
 * exceeds `bounds.height` becomes scrollable via mouse wheel and touch drag.
 *
 * Call the returned `updateScrollBounds()` after adding/removing children
 * so the scroll range stays correct.
 */
export function createScrollableContainer(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  bounds: ScrollBounds,
): {
  container: Phaser.GameObjects.Container;
  updateScrollBounds: () => void;
  scrollTo: (y: number) => void;
} {
  const inner = scene.add.container(bounds.x, bounds.y);
  parent.add(inner);

  // Geometry mask — position in world coords
  const maskGraphics = scene.add.graphics();
  maskGraphics.setVisible(false);

  const redrawMask = () => {
    maskGraphics.clear();
    maskGraphics.fillStyle(0xffffff);
    // Walk the full parent chain to compute world coordinates
    let worldX = bounds.x;
    let worldY = bounds.y;
    let node: Phaser.GameObjects.Container | null = parent;
    while (node) {
      worldX += node.x;
      worldY += node.y;
      node = node.parentContainer ?? null;
    }
    maskGraphics.fillRect(worldX, worldY, bounds.width, bounds.height);
  };
  redrawMask();
  inner.setMask(maskGraphics.createGeometryMask());

  let scrollOffset = 0;
  let contentHeight = 0;

  const clampScroll = () => {
    const maxScroll = Math.max(0, contentHeight - bounds.height);
    scrollOffset = Phaser.Math.Clamp(scrollOffset, 0, maxScroll);
    inner.y = bounds.y - scrollOffset;
  };

  // Interactive hit zone for scroll events
  const hitZone = scene.add.rectangle(
    bounds.x + bounds.width / 2,
    bounds.y + bounds.height / 2,
    bounds.width,
    bounds.height,
    0x000000,
    0,
  );
  hitZone.setInteractive();
  parent.add(hitZone);

  // Mouse wheel scroll. Per-GameObject wheel callback is (pointer, dx, dy, dz, event);
  // most mice put vertical scroll on dy. Fall back to dz for trackpads that emit it.
  hitZone.on('wheel', (_pointer: Phaser.Input.Pointer, _dx: number, dy: number, dz: number) => {
    const delta = dy !== 0 ? dy : dz;
    if (delta === 0) return;
    scrollOffset += delta > 0 ? 30 : -30;
    clampScroll();
  });

  // Touch drag scroll
  let dragStartY = 0;
  let dragStartOffset = 0;

  hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    dragStartY = pointer.y;
    dragStartOffset = scrollOffset;
  });

  scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
    if (!pointer.isDown) return;
    const dy = dragStartY - pointer.y;
    scrollOffset = dragStartOffset + dy;
    clampScroll();
  });

  const updateScrollBounds = () => {
    // Measure tallest child bottom edge
    let maxY = 0;
    inner.each((child: Phaser.GameObjects.GameObject) => {
      const go = child as Phaser.GameObjects.Container | Phaser.GameObjects.Text | Phaser.GameObjects.Rectangle;
      if ('y' in go && 'height' in go) {
        const bottom = (go.y || 0) + ((go as any).displayHeight || (go as any).height || 0);
        if (bottom > maxY) maxY = bottom;
      }
    });
    contentHeight = maxY;
    clampScroll();
    redrawMask();
  };

  const scrollTo = (y: number) => {
    scrollOffset = y;
    clampScroll();
  };

  // Clean up mask graphics when parent is destroyed
  parent.once('destroy', () => {
    maskGraphics.destroy();
  });

  return { container: inner, updateScrollBounds, scrollTo };
}
